import axios, { AxiosError, AxiosInstance } from 'axios';
import { getIdToken, refreshToken } from './firebase';
import { environment } from './environment';

// Environment-based configuration with API Gateway support
const getApiBaseUrl = (): string => {
  // Check for API Gateway URL first (future state)
  const apiGatewayUrl = import.meta.env.VITE_API_GATEWAY_URL;
  if (apiGatewayUrl && apiGatewayUrl !== 'undefined') {
    // API Gateway will handle routing to backend services
    return `${apiGatewayUrl}/api/v2`;
  }
  
  // Check for direct backend URL (current state)
  const viteApiUrl = import.meta.env.VITE_API_BASE_URL;
  if (viteApiUrl && viteApiUrl !== 'undefined') {
    return viteApiUrl;
  }
  
  // Check if running in production
  const isProduction = import.meta.env.PROD;

  if (isProduction) {
    // In production, require explicit configuration
    throw new Error('VITE_API_BASE_URL environment variable is required in production');
  }

  // Development fallback
  return 'http://localhost:8080/api/v2';
};

const getWsBaseUrl = (): string => {
  // Check for API Gateway WebSocket URL first (future state)
  const apiGatewayWsUrl = import.meta.env.VITE_API_GATEWAY_WS_URL;
  if (apiGatewayWsUrl && apiGatewayWsUrl !== 'undefined') {
    return apiGatewayWsUrl;
  }
  
  // Check for direct WebSocket URL (current state)
  const viteWsUrl = import.meta.env.VITE_WS_BASE_URL;
  if (viteWsUrl && viteWsUrl !== 'undefined') {
    return viteWsUrl;
  }
  
  // Check if running in production
  const isProduction = import.meta.env.PROD;

  if (isProduction) {
    // In production, require explicit configuration
    throw new Error('VITE_WS_BASE_URL environment variable is required in production');
  }

  // Development fallback
  return 'ws://localhost:8080';
};

// API configuration
export const API_BASE_URL = getApiBaseUrl();
export const WS_BASE_URL = getWsBaseUrl();

// Update environment module with resolved URLs
environment.apiBaseUrl = API_BASE_URL;
environment.wsBaseUrl = WS_BASE_URL;


// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  async (config) => {
    // Don't force refresh on every request - only get current token
    const token = await getIdToken(false);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
apiClient.interceptors.response.use(
  async (response) => {
    // Check if backend signals that a token refresh is needed (e.g., claims were repaired)
    const forceRefresh = response.headers['x-force-token-refresh'];
    if (forceRefresh === 'true') {
      console.log('[API] Backend signaled token refresh required - forcing token refresh');
      try {
        // Force token refresh to get new claims
        await refreshToken();
      } catch (refreshError) {
        console.error('[API] Failed to refresh token after claims repair:', refreshError);
      }
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    // Initialize retry metadata if not present
    if (!originalRequest._retryCount) {
      originalRequest._retryCount = 0;
    }

    // Maximum retry configuration
    const MAX_RETRIES = 3;
    const RETRY_DELAY_BASE = 1000; // 1 second base delay
    const MAX_RETRY_DELAY = 30000; // 30 seconds max delay

    // Don't retry if we've exceeded max retries
    if (originalRequest._retryCount >= MAX_RETRIES) {
      console.error('[API] Max retries exceeded:', {
        url: originalRequest.url,
        retries: originalRequest._retryCount,
        status: error.response?.status
      });
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized - Token expiration
    if (error.response?.status === 401 && !originalRequest._tokenRefreshAttempted) {
      const errorData = error.response?.data as any;
      const isTokenError = errorData?.error?.message?.toLowerCase().includes('token') ||
                          errorData?.error?.message?.toLowerCase().includes('claims') ||
                          errorData?.error?.message?.toLowerCase().includes('expired');
      
      if (isTokenError) {
        originalRequest._tokenRefreshAttempted = true;

        try {
          // Force token refresh
          const newToken = await refreshToken();
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return apiClient(originalRequest);
          }
        } catch (refreshError) {
          console.error('[API] Token refresh failed:', refreshError);
          // Redirect to login if refresh fails
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
    }

    // Don't retry on certain error types
    const NO_RETRY_STATUSES = [
      400, // Bad Request
      401, // Unauthorized (after token refresh attempt)
      403, // Forbidden
      404, // Not Found
      422, // Unprocessable Entity
    ];

    if (error.response && NO_RETRY_STATUSES.includes(error.response.status)) {
      // Handle specific status codes
      let errorMessage = 'An error occurred';
      
      if (error.response.status === 404) {
        errorMessage = 'Resource not found. Please check the endpoint.';
      } else if (error.response.status === 403) {
        errorMessage = 'Access forbidden. Please check your permissions.';
      } else if (error.response.status === 401) {
        errorMessage = 'Authentication failed. Please login again.';
      } else {
        errorMessage = (error.response.data as any)?.error?.message || errorMessage;
      }
      
      return Promise.reject({
        status: error.response.status,
        message: errorMessage,
        data: error.response.data,
      });
    }

    // Handle rate limiting (429)
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'];
      const delay = retryAfter ? parseInt(retryAfter) * 1000 : RETRY_DELAY_BASE * Math.pow(2, originalRequest._retryCount);
      
      console.warn('[API] Rate limited. Retrying after delay:', {
        url: originalRequest.url,
        delay: delay / 1000 + 's',
        attempt: originalRequest._retryCount + 1
      });

      originalRequest._retryCount++;
      
      await new Promise(resolve => setTimeout(resolve, Math.min(delay, MAX_RETRY_DELAY)));
      return apiClient(originalRequest);
    }

    // Retry on network errors and 5xx server errors
    const shouldRetry = !error.response || (error.response.status >= 500 && error.response.status < 600);
    
    if (shouldRetry) {
      // Calculate exponential backoff delay
      const delay = Math.min(RETRY_DELAY_BASE * Math.pow(2, originalRequest._retryCount), MAX_RETRY_DELAY);
      
      console.warn('[API] Retrying request:', {
        url: originalRequest.url,
        attempt: originalRequest._retryCount + 1,
        maxAttempts: MAX_RETRIES,
        delay: delay / 1000 + 's',
        status: error.response?.status || 'network error'
      });

      originalRequest._retryCount++;
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
      return apiClient(originalRequest);
    }

    // Handle other errors
    if (error.response) {
      // Server responded with error
      let errorMessage = 'An error occurred';
      
      if (error.response.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else {
        errorMessage = (error.response.data as any)?.error?.message || errorMessage;
      }
      
      return Promise.reject({
        status: error.response.status,
        message: errorMessage,
        data: error.response.data,
      });
    } else if (error.request) {
      // Request made but no response
      return Promise.reject({
        status: 0,
        message: 'Network error - please check your connection',
      });
    } else {
      // Request setup error
      return Promise.reject({
        status: 0,
        message: error.message || 'Request could not be sent. Check your network connection and try again.',
      });
    }
  }
);

// API response types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
  metadata?: {
    timestamp: string;
    requestId?: string;
    pagination?: {
      count: number;
      nextCursor?: string;
      hasMore: boolean;
    };
  };
}

// Generic API methods
export const api = {
  get: <T = any>(url: string, params?: any) => 
    apiClient.get<ApiResponse<T>>(url, { params }).then(res => res.data),
  
  post: <T = any>(url: string, data?: any) => 
    apiClient.post<ApiResponse<T>>(url, data).then(res => res.data),
  
  put: <T = any>(url: string, data?: any) => 
    apiClient.put<ApiResponse<T>>(url, data).then(res => res.data),
  
  patch: <T = any>(url: string, data?: any) => 
    apiClient.patch<ApiResponse<T>>(url, data).then(res => res.data),
  
  delete: <T = any>(url: string) => 
    apiClient.delete<ApiResponse<T>>(url).then(res => res.data),
};

// Export configured axios instance for direct use
export default apiClient;